import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { formatMinutes } from '@/lib/perf';
import { Button, Card, CardBody, CardHeader, Field, Input, Spinner, Textarea, useToast } from '@/components/ui';

/**
 * Load limits of a project's performance tests: the most anyone may start (Project Admins change it) and extra hosts tests
 * may reach besides the app's own address. Members are also held to 500 users / 30 minutes and the agents' capacity.
 */
export function LoadLimitsCard({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['load-limits', projectId], queryFn: () => api.loadLimits(projectId) });
  const [maxVus, setMaxVus] = useState(500);
  const [maxMinutes, setMaxMinutes] = useState(30);
  const [maxRps, setMaxRps] = useState('');
  const [hosts, setHosts] = useState('');
  useEffect(() => {
    if (!q.data) return;
    setMaxVus(q.data.limits.maxVus);
    setMaxMinutes(Math.round(q.data.limits.maxDurationS / 60));
    setMaxRps(q.data.limits.maxRps ? String(q.data.limits.maxRps) : '');
    setHosts(q.data.limits.allowedHosts.join('\n'));
  }, [q.data]);
  const save = useMutation({
    mutationFn: () =>
      api.setLoadLimits(projectId, {
        maxVus,
        maxDurationS: Math.max(1, maxMinutes) * 60,
        maxRps: maxRps.trim() ? Number(maxRps) : null,
        allowedHosts: hosts.split(/[\s,]+/).map((h) => h.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      toast.success('Load limits saved');
      qc.invalidateQueries({ queryKey: ['load-limits', projectId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Card className="lg:col-span-3">
      <CardHeader
        title="Performance tests: load limits"
        description={
          q.data
            ? `The most load anyone may start in this project. Members can start up to ${q.data.memberLimit.vus} users for ${formatMinutes(q.data.memberLimit.durationS)}, and only what the load agents can carry; above that, a Project Admin.`
            : undefined
        }
      />
      <CardBody>
        {q.isPending ? (
          <Spinner />
        ) : (
          <form
            className="grid gap-4 md:grid-cols-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <Field label="Most users">{(id) => <Input id={id} type="number" min={1} max={100_000} disabled={!canEdit} value={maxVus} onChange={(e) => setMaxVus(Number(e.target.value))} />}</Field>
            <Field label="Longest run (minutes)">{(id) => <Input id={id} type="number" min={1} max={10_080} disabled={!canEdit} value={maxMinutes} onChange={(e) => setMaxMinutes(Number(e.target.value))} />}</Field>
            <Field label="Most requests per second" hint="Empty: no extra limit">
              {(id) => <Input id={id} type="number" min={1} disabled={!canEdit} value={maxRps} onChange={(e) => setMaxRps(e.target.value)} placeholder="no limit" />}
            </Field>
            <Field label="Other hosts tests may load" hint="Besides the app’s address. One per line, e.g. api.example.com or *.example.com">
              {(id) => <Textarea id={id} rows={2} disabled={!canEdit} value={hosts} onChange={(e) => setHosts(e.target.value)} placeholder="login.example.com" className="min-h-0 font-mono text-xs" />}
            </Field>
            {canEdit && (
              <div className="flex justify-end md:col-span-4">
                <Button type="submit" loading={save.isPending} icon={<Save className="size-4" />}>
                  Save limits
                </Button>
              </div>
            )}
          </form>
        )}
      </CardBody>
    </Card>
  );
}
